import i18n from '@app/i18n';

// Browser `fetch` rejects with a TypeError whose message differs per engine.
const NETWORK_ERROR_MESSAGES = [
  'failed to fetch', // Chrome / Edge
  'networkerror when attempting to fetch resource.', // Firefox
  'load failed', // Safari
  'network request failed',
];

// Represents a server-localized, user-facing error message.
export class ServerMessageError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ServerMessageError';
  }
}

// A failed response with no parseable body; `status` is kept for diagnostics.
export class HttpStatusError extends Error {
  readonly status: number;

  constructor(status: number) {
    super(i18n.t('toasts:errors.serverError'));
    this.name = 'HttpStatusError';
    this.status = status;
  }
}

interface AxiosLikeError {
  isAxiosError?: boolean;
  code?: string;
  response?: { status?: number; data?: unknown };
}

const asAxiosError = (error: unknown): AxiosLikeError | null =>
  typeof error === 'object' &&
  error !== null &&
  (error as AxiosLikeError).isAxiosError === true
    ? (error as AxiosLikeError)
    : null;

const apiMessageOf = (axiosError: AxiosLikeError): string => {
  const data = axiosError.response?.data;
  if (typeof data === 'object' && data !== null) {
    const { message } = data as { message?: unknown };
    if (typeof message === 'string') return message.trim();
  }
  return '';
};

const isAbort = (error: unknown): boolean =>
  error instanceof Error &&
  (error.name === 'AbortError' || error.name === 'TimeoutError');

const messageOf = (error: unknown): string => {
  if (typeof error === 'string') return error;
  if (error instanceof Error) return error.message;
  return '';
};

// Converts any error into a translated, user-friendly message.
export const describeError = (error: unknown, fallback?: string): string => {
  if (error instanceof ServerMessageError && error.message.trim()) {
    return error.message;
  }
  if (error instanceof HttpStatusError) {
    return i18n.t('toasts:errors.serverError');
  }
  if (isAbort(error)) return i18n.t('toasts:errors.requestTimeout');

  const axiosError = asAxiosError(error);
  if (axiosError) {
    const apiMessage = apiMessageOf(axiosError);
    if (apiMessage) return apiMessage;
    if (axiosError.code === 'ERR_CANCELED') {
      return i18n.t('toasts:errors.requestTimeout');
    }
    if (axiosError.code === 'ERR_NETWORK') {
      return i18n.t('toasts:errors.networkError');
    }
    if (axiosError.response?.status) {
      return i18n.t('toasts:errors.serverError');
    }
  }

  const message = messageOf(error).trim().toLowerCase();

  if (NETWORK_ERROR_MESSAGES.includes(message)) {
    return i18n.t('toasts:errors.networkError');
  }
  if (/^request failed with status \d+/.test(message)) {
    return i18n.t('toasts:errors.serverError');
  }
  return fallback ?? i18n.t('toasts:errors.unexpectedError');
};

export const asServerError = (value: unknown): unknown => {
  if (value instanceof Error) return value;
  if (
    typeof value !== 'object' ||
    value === null ||
    typeof (value as { message?: unknown }).message !== 'string' ||
    !(value as { message: string }).message.trim()
  ) {
    return value;
  }
  const error = new ServerMessageError((value as { message: string }).message);
  return Object.assign(error, value);
};

export const toServerError = (message: unknown): Error =>
  typeof message === 'string' && message.trim()
    ? new ServerMessageError(message)
    : new Error('');
